/**
 * leave-guard — confirm ТОЛЬКО на явных разрушающих действиях (run-persistence-lifecycle.md
 * T-C, owner-directive 2026-06-13). Переспроектирован: раньше ЛЮБОЙ уход с доски
 * (навигация / рефреш / beforeunload / «Новая игра» / «Завершить заход») спрашивал
 * подтверждение. Теперь прогресс ВСЕГДА в кэше (T-B) и зачёт происходит при любом
 * завершении (T-D) → терять нечего, «случайно закрыть» невозможно. Поэтому:
 *
 *   - Навигация на другую поверхность / рефреш / beforeunload / закрытие вкладки —
 *     ТИХОЕ сохранение, БЕЗ confirm (партия в кэше, возврат через «Играть» — T-B).
 *   - «Завершить заход» (явный финиш) — confirm ОСТАЁТСЯ (`confirmFinish`).
 *   - «Новая игра» mid-game (явный сброс) — confirm ОСТАЁТСЯ (`confirmNewGame`): при
 *     подтверждении текущий счёт ЗАСЧИТЫВАЕТСЯ перед сбросом (T-D `new_game`, RP-7).
 *
 * «At stake» теперь = «игрок инициировал ЯВНОЕ разрушающее действие», НЕ «существует
 * активная партия» (I-8). Daily-нюанс: «Новая игра» в daily НЕДОСТУПНА (одна попытка);
 * «К доске дня» — это навигация (тихо, партия дня кэширована и возобновляема).
 *
 * Один примитив (`confirmDialog`) на оба явных действия. beforeunload-guard снят
 * (`guardBeforeUnload` — no-op для обратной совместимости вызова в app.ts до чистки).
 */

import { confirmDialog } from '../components/confirm-dialog.js';
import { useGameStore, type GameStoreState } from '../store/game-store.js';
import { t } from './i18n/index.js';

/**
 * Идёт ли ЗАЧЁТНАЯ партия, для которой явное разрушающее действие имеет смысл
 * подтверждать. Используется ТОЛЬКО явными действиями («Завершить заход» / «Новая
 * игра») — НЕ навигацией/рефрешем (T-C / I-8). Итоги/реплей/хаб — не at-stake.
 */
export function isRunAtStake(s: GameStoreState = useGameStore.getState()): boolean {
  if (s.phase !== 'board' || s.finishReason) return false;
  if (s.preStart) return false; // pre-start превью — ничего не начато (зачёта нет)
  if (s.context.kind === 'daily') return true; // daily — заход всегда «в игре»
  return s.moves.length >= 1; // all_time / casual — только после хода
}

/**
 * Подтвердить ЗАВЕРШЕНИЕ захода («Завершить заход» — результат засчитывается). Явное
 * разрушающее действие → confirm остаётся (T-C). Честный текст: не «потеряешь», а
 * «завершится и засчитается».
 */
export async function confirmFinish(): Promise<boolean> {
  const s = useGameStore.getState();
  if (!isRunAtStake(s)) return true;
  const daily = s.context.kind === 'daily';
  // Диалог строится в момент явного действия (показа) → t() = актуальный язык.
  return confirmDialog({
    title: t('game.leaveGuard.finishTitle'),
    message: daily
      ? t('game.leaveGuard.finishMessage')
      : t('game.leaveGuard.newGameMessageCasual'),
    confirmLabel: t('game.leaveGuard.finishConfirm'),
    cancelLabel: t('game.leaveGuard.finishCancel'),
  });
}

/**
 * Подтвердить «Новую игру» mid-game (явный сброс — all_time / casual). Явное
 * разрушающее действие → confirm остаётся (T-C); при подтверждении текущий счёт
 * ЗАСЧИТЫВАЕТСЯ перед сбросом (T-D `new_game`, RP-7) — поэтому текст «счёт будет
 * засчитан», а не «потеряется». Daily сюда не попадает («Новая игра» недоступна).
 */
export async function confirmNewGame(): Promise<boolean> {
  const s = useGameStore.getState();
  if (!isRunAtStake(s)) return true;
  // Диалог строится в момент явного действия (показа) → t() = актуальный язык.
  return confirmDialog({
    title: t('game.leaveGuard.newGameTitle'),
    message: t('game.leaveGuard.newGameMsg'),
    confirmLabel: t('game.leaveGuard.newGameConfirm'),
    cancelLabel: t('game.leaveGuard.newGameCancel'),
  });
}

// =============================================================================
// beforeunload / OAuth-redirect — НЕТ confirm (T-C: тихое сохранение)
// =============================================================================

/**
 * No-op (T-C): раньше подавляла нативный beforeunload-prompt перед намеренным уходом
 * (OAuth-редирект), чтобы не было двойного подтверждения. Теперь beforeunload-guard
 * снят (рефреш/закрытие — тихо, всё в кэше), поэтому подавлять нечего. Оставлена как
 * no-op для совместимости вызова (`auth-flow`) — чистится отдельным рефактором.
 */
export function suppressNextUnloadGuard(): void {
  /* no-op (T-C) — beforeunload больше не спрашивает (тихое сохранение) */
}

/**
 * No-op (T-C): beforeunload больше НЕ показывает нативный prompt при незавершённой
 * игре — рефреш/закрытие вкладки тихо сохраняют прогресс в кэш (T-B), возврат через
 * «Играть» восстанавливает партию. Оставлена как no-op для совместимости вызова
 * (`app.ts` слушатель) — чистится отдельным рефактором. Параметр не используется.
 */
export function guardBeforeUnload(_e: BeforeUnloadEvent): void {
  /* no-op (T-C) — нет prompt'а; прогресс уже в кэше */
}
